/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import PostType from '@linc-technologies/linced-common/enums/post-type';
import moment from 'moment';
import { alias } from '@ember/object/computed';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class MySubscriptionsController extends Controller {
  @action
  setPostType(value) {
    this.postType = value;
  }

  // Services
  @service
  paperToaster;

  @service
  roles;

  @service
  session;

  @service
  store;

  @tracked
  subscriptionDialogOpen = false;
  @tracked
  saveInProgress = false;
  @tracked personType = PersonType.LEARNER;
  @tracked postType = PostType.POST;
  @tracked selectedGroup = null;
  @tracked selectedPerson = null;
  @tracked selectedPage = null;
  @tracked selectedTag = null;
  @tracked errorMessage = null;
  @tracked
  subscriptionToDelete = null;
  @tracked
  confirmDeleteDialogOpen = false;
  @tracked
  deleteInProgress = false;
  PostType = PostType;

  @alias('session.school.heroPremierEnabled')
  heroPremierEnabled;

  @alias('session.school.heroSuiteEnabled')
  heroSuiteEnabled;

  @alias('session.school.communicationsExtensionKitEnabled')
  commsKitEnabled;

  @alias('session.school.heroStarterPackEnabled')
  heroStarterPackEnabled;

  @computed('heroPremierEnabled', 'commsKitEnabled')
  get displayUpgradeLabel() {
    if (this.heroStarterPackEnabled) {
      return !this.commsKitEnabled;
    }
    return !this.heroPremierEnabled;
  }

  @computed('heroPremierEnabled', 'commsKitEnabled')
  get upgradeLabel() {
    if (this.heroStarterPackEnabled) {
      if (!this.commsKitEnabled) {
        return 'Subscribing to Staff Feed updates is available in the Communications Kit.';
      }
    }
    if (!this.heroPremierEnabled) {
      return 'Subscribing to Staff Feed updates is available in Hero Premier.';
    }
    return '';
  }

  @computed(
    'postType',
    'selectedGroup',
    'selectedPerson',
    'selectedPage',
    'selectedTag',
    'roles.currentUserIsRestricted',
  )
  get hasFilter() {
    if (this.postType === PostType.STAFF_NOTICE) {
      // No filters apply to Staff Notices.
      return true;
    }
    if (this.roles.currentUserIsRestricted) {
      // Staff wih restricted access must choose a page or tag along with a person or group  to save subscription
      return (
        (this.selectedGroup || this.selectedPerson) &&
        (this.selectedPage || this.selectedTag)
      );
    }
    return (
      this.selectedGroup ||
      this.selectedPerson ||
      this.selectedPage ||
      this.selectedTag
    );
  }

  @computed('personType', 'model.{learnerPages,staffPages}', 'selectedPerson')
  get pages() {
    switch (this.personType) {
      case PersonType.LEARNER:
        if (!isEmpty(this.selectedPerson)) {
          return this.store.query('posts/page', {
            type: 1,
            learnerId: this.selectedPerson.id,
          });
        }
        return this.model.learnerPages;
      case PersonType.STAFF:
        return this.model.staffPages;
    }
    return [];
  }

  @computed('selectedPage', 'model.{tagCategories,tags}')
  get tags() {
    let tags = this.model.tags;
    if (this.selectedPage) {
      tags = tags.filter((tag) => {
        return this.selectedPage.tagCategories.find(
          (item) => item.id === tag.get('category.id'),
        );
      });
    }
    return tags
      .slice()
      .sort((a, b) =>
        (a.get('category.label') || '').localeCompare(
          b.get('category.label') || '',
        ),
      );
  }

  @computed('model.groups.[]')
  get groups() {
    return this.model.groups.filter((group) => {
      if (group.type === GroupType.GROUP) {
        // My Groups - filter down to only those which the current user is associated with.
        return !!group.currentStaff.find(
          (item) => item.personId === this.get('session.person.id'),
        );
      }
      return true;
    });
  }

  currentYear = moment().year();

  @action
  addSubscription() {
    this.subscriptionDialogOpen = true;
  }

  @action
  selectGroup(group) {
    this.selectedGroup = group;
    this.selectedPerson = null;
  }

  @action
  selectPage(page) {
    this.selectedPage = page;
    if (!isEmpty(this.selectedTag)) {
      if (!findBy(this.tags, 'id', this.get('selectedTag.id'))) {
        // The selected tag isn't associated with this page.  Clear it.
        this.selectedTag = null;
      }
    }
  }

  @action
  selectTag(tag) {
    this.selectedTag = tag;
  }

  @action
  selectPerson(person) {
    this.selectedPerson = person;
    this.selectedGroup = null;
    this.selectedPage = null;
  }

  @action
  closeSubscriptionDialog() {
    this.resetSubscriptionForm();
    this.subscriptionDialogOpen = false;
  }

  @action
  saveSubscription() {
    this.saveInProgress = true;
    let subscription = this.store.createRecord('notifications/subscription', {
      schoolId: this.session.school,
      subscriberId: this.session.person,
      groupId: this.selectedGroup,
      personId: this.selectedPerson,
      postType: this.postType,
    });
    if (this.postType === PostType.POST) {
      // Page and Tag filters only apply to regular Posts.
      subscription.setProperties({
        pageId: this.selectedPage,
        tagId: this.selectedTag,
      });
    }
    subscription
      .save()
      .then(() => {
        this.resetSubscriptionForm();
        this.subscriptionDialogOpen = false;

        this.paperToaster.show(`Subscription created.`, {
          duration: 8000,
          position: 'top right',
          toastClass: 'success',
        });

        this.model.subscriptions.update();
      })
      .catch(() => {
        this.saveInProgress = false;
        this.paperToaster.show(`Failed to create subscription.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      });
  }

  @action
  confirmDelete(subscription) {
    this.subscriptionToDelete = subscription;
    this.confirmDeleteDialogOpen = true;
    this.deleteInProgress = false;
  }

  @action
  cancelDelete() {
    this.subscriptionToDelete = null;
    this.confirmDeleteDialogOpen = false;
    this.deleteInProgress = false;
  }

  @action
  doDelete() {
    this.deleteInProgress = true;
    this.subscriptionToDelete
      .destroyRecord()
      .then(() => {
        this.subscriptionToDelete = null;
        this.confirmDeleteDialogOpen = false;
        this.deleteInProgress = false;
        this.paperToaster.show(`Subscription deleted.`, {
          duration: 8000,
          position: 'top right',
          toastClass: 'success',
        });

        this.model.subscriptions.update();
      })
      .catch(() => {
        this.deleteInProgress = false;
        this.paperToaster.show(`Failed to delete subscription.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      });
  }

  resetSubscriptionForm() {
    this.saveInProgress = false;
    this.personType = PersonType.LEARNER;
    this.postType = PostType.POST;
    this.selectedGroup = null;
    this.selectedPerson = null;
    this.selectedPage = null;
    this.selectedTag = null;
    this.errorMessage = null;
  }
}
